import React, { useState, useRef } from 'react';
import './AddBrandModal.css';

const AddBrandModal = ({ isOpen, onClose, onSave }) => {
  const [brandName, setBrandName] = useState('');
  const [brandLogo, setBrandLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBrandLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = () => {
    setBrandName('');
    setBrandLogo(null);
    setLogoPreview(null);
    setError('');
    onClose();
  };

  const handleSave = async () => {
    // Validate brand name before saving
    if (!brandName || !brandName.trim()) {
      setError('Please enter a brand name');
      return;
    }
    
    if (onSave) {
      setSaving(true);
      setError('');
      try {
        await onSave({
          name: brandName,
          logo: brandLogo,
          logoPreview: logoPreview
        });
        // Parent will close modal on success, just reset local state
        setBrandName('');
        setBrandLogo(null);
        setLogoPreview(null);
        setError('');
        setSaving(false);
      } catch (err) {
        console.error('Save brand error in modal:', err);
        setError(err.response?.data?.error || err.message || 'Failed to save brand');
        setSaving(false);
      }
    }
  };

  return (
    <div className="add-brand-modal-overlay" onClick={onClose}>
      <div className="add-brand-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="add-brand-modal-header">
          <h2 className="add-brand-modal-title">Add new brand</h2>
        </div>

        <div className="add-brand-modal-content">
          {/* Brand Logo Upload */}
          <div className="add-brand-logo-section">
            <div className="add-brand-logo-wrapper" onClick={handleLogoClick}>
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt="Brand logo preview" 
                  className="add-brand-logo-preview"
                />
              ) : (
                <div className="add-brand-logo-placeholder">
                  <svg 
                    width="32" 
                    height="32" 
                    viewBox="0 0 32 32" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M16 6V26M6 16H26" 
                      stroke="#DADADA" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoChange}
                accept="image/*"
                className="add-brand-logo-input"
              />
            </div>
          </div>

          {/* Brand Name Input */}
          <div className="add-brand-input-section">
            <label className="add-brand-input-label">Write brand name</label>
            <div className="add-brand-input-wrapper">
              <input
                type="text"
                placeholder="Brand Name"
                value={brandName}
                onChange={(e) => { setBrandName(e.target.value); setError(''); }}
                className="add-brand-input"
                disabled={saving}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{ color: '#e74c3c', fontSize: '13px', marginTop: '4px', padding: '0 4px' }}>
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="add-brand-actions">
            <button 
              type="button" 
              className="add-brand-btn add-brand-btn-cancel"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="add-brand-btn add-brand-btn-save"
              onClick={handleSave}
              disabled={saving}
              style={saving ? { opacity: 0.7 } : {}}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBrandModal;
